import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { isPortrait } from '../utils/size';
import { copyFor } from '../copy/copy';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
    padding: 12px 0;
    ${props =>
        props.isExpandable &&
        `   position: absolute;
            width: 100%;`}
    ${props => props.isShortRow && `margin-bottom: -10px;`}
`;

const Textarea = styled.textarea`
    height: 100px;
    border-radius: 2px;
    border: solid 1px
        ${props =>
            (!props.default || props.active) && props.valid ? '#f1f1ee' : '#ff245e'};
    color: #333;
    font-family: Averta;
    font-size: ${() => (isPortrait() ? 16 : 12)}px;
    line-height: ${() => (isPortrait() ? 20 : 16)}px;
    padding: 8px;
    resize: none;
    transition: height 0.6s;
    -webkit-appearance: none;
    ${props =>
        props.isExpandable &&
        !isPortrait() &&
        `
                position: absolute;
                width: 100%;
                bottom: -60px;
                ${(props.active || props.textAreaFocused) && `height: 210px !important;`};
            `}

    ${() =>
        isPortrait() &&
        `   height: 205px;
            margin-left: 0;
            margin-right: 0;
    `};

    &:focus,
    &:active {
        outline: none;
        border: solid 1px #979191;
    }

    ${props =>
        !props.scrollableDesign
            ? `height: ${isPortrait() ? 195 : !props.includesTopic ? 73 : 175}px`
            : `height: ${isPortrait() ? 210 : 135}px`};
`;

const LabelContainer = styled.div`
    pointer-events: none;
    display: flex;
    justify-content: space-between;
    position: absolute;
    top: 0;
    width: 100%;
    transition: top 0.6s;
    ${props =>
        props.isExpandable && !isPortrait()
            ? (props.active || props.textAreaFocused) && ` top: -138px;`
            : null};
`;

const Label = styled.label`
    pointer-events: none;
    border-radius: 5px;
    font-family: Averta;
    left: 5px;
    letter-spacing: 1px;
    line-height: 1.5;
    margin-bottom: 0.5em;
    padding: 0 5px;
    position: absolute;
    top: ${props => (props.default ? 20 : 7)}px;
    background-color: ${props =>
        props.labelBackgroundColor ? props.labelBackgroundColor : `white`};
    color: ${props =>
        props.active || props.valid ? `#979191` : `#ff245e`};
    font-size: ${props => (props.default ? 14 : 8)}px;
    font-weight: ${props => (props.default ? `400` : `600`)};
    text-transform: ${props => (props.default ? `capitalize` : `uppercase`)};
    transition: all 250ms ease;
    z-index: 2;
`;

class TextareaGroup extends React.Component {
    constructor(props){
        super(props);
        this.textAreaLabelRef = React.createRef();
        this.state = {labelHeight: 0};
    }

    static propTypes = {
        autocomplete: PropTypes.string,
        autoFocus: PropTypes.bool,
        autoFocusTimeout: PropTypes.number,
        clearSubmitErrors: PropTypes.func,
        isExpandable: PropTypes.bool,
        input: PropTypes.object,
        scrollableDesign: PropTypes.bool,
        label: PropTypes.string.isRequired,
        labelBackgroundColor: PropTypes.string,
        maxLength: PropTypes.number,
        meta: PropTypes.object.isRequired,
        placeholder: PropTypes.string,
        primary: PropTypes.bool,
        readOnly: PropTypes.bool,
        submissionError: PropTypes.bool,
        textareaColor: PropTypes.string,
        title: PropTypes.string,
        toggleTextAreaFocused: PropTypes.func,
        textAreaFocused: PropTypes.bool
    };

    clearFormLevelSubmissionErrors() {
        if (typeof this.props.clearSubmitErrors === 'function' && this.props.input.name) {
            this.props.clearSubmitErrors(this.props.input.name);
        }
    }

    componentDidUpdate(){
        //adjust multiline label text to correct y position
        const textAreaLabel = this.textAreaLabelRef.current;
        if (!textAreaLabel) return;
        if (!this.props.active && this.props.input.value.length > 0){
            const labelHeight = textAreaLabel.offsetHeight;
            if (this.state.labelHeight !== labelHeight) this.setState({ labelHeight });
        }
        else if (this.state.labelHeight > 12) this.setState({ labelHeight: 0 });
    }

    get optionalLabelText(){
        return this.props.isOptional ?  ' ' + copyFor('formFields', 'optional') : ''
    }

    render() {
        const defaultState =
            !this.props.meta.active &&
            !this.props.meta.valid &&
            (this.props.meta.pristine || this.props.input.value === '');
        const selected = this.props.meta.active;
        const error =
            this.props.submissionError ||
            (this.props.meta.invalid &&
                this.props.meta.touched &&
                this.props.input.value !== '');
        return (
            <Container
                isShortRow={this.props.isShortRow}
                style={this.props.containerStyle || {}}
            >
                <LabelContainer
                    isExpandable={this.props.isExpandable}
                    active={this.props.meta.active}
                    textAreaFocused={this.props.textAreaFocused}
                >
                    <Label
                        ref={this.textAreaLabelRef}
                        {...this.props.input}
                        {...this.props.meta}
                        readOnly={this.props.readOnly}
                        valid={!error}
                        default={defaultState && !selected && !this.props.input.value && !this.props.active}
                        labelBackgroundColor={this.props.labelBackgroundColor}
                        scrollableDesign={this.props.scrollableDesign}
                        isExpandable={this.props.isExpandable}
                        style={this.state.labelHeight > 12 && !this.props.active ? {top: 0, width: '97%'} : {}}
                    >
                        {error && !selected ? this.props.meta.error : this.props.label + this.optionalLabelText}
                    </Label>
                </LabelContainer>
                <Textarea
                    {...this.props.input}
                    {...this.props.meta}
                    value={this.props.input.value.split('<br />').join('\n')}
                    valid={!error}
                    textAreaFocused={this.props.textAreaFocused}
                    isExpandable={this.props.isExpandable}
                    scrollableDesign={this.props.scrollableDesign}
                    textareaColor={this.props.textareaColor}
                    style={this.props.style}
                    maxLength={this.props.maxLength}
                    placeholder={this.props.placeholder}
                    autocomplete={this.props.autocomplete}
                    name={this.props.title}
                    onFocus={() => {
                        this.props.input.onFocus();
                        this.setState({ focused: true });
                        if (
                            this.props.isExpandable &&
                            !this.props.scrollableDesign &&
                            !!this.props.toggleTextAreaFocused
                        ) {
                            this.props.toggleTextAreaFocused();
                        }

                        this.clearFormLevelSubmissionErrors(this.props.input.name);
                    }}
                    onBlur={() => {
                        this.props.input.onBlur();
                        this.setState({ focused: false });
                        setTimeout(() => {
                            if (
                                this.props.isExpandable &&
                                !this.props.scrollableDesign &&
                                !!this.props.toggleTextAreaFocused
                            ) {
                                this.props.toggleTextAreaFocused();
                            }
                        }, 0);
                    }}
                    id="textarea"
                    readOnly={this.props.readOnly}
                />
            </Container>
        );
    }
}

export default TextareaGroup;
